/** @jsx isolateComponent */

import { ReactNode } from 'react';
import { Interpolation } from '@emotion/css';

import { useUniversalNavContext } from '@zapier/universal-nav/dist/hooks/useUniversalNavContext';
import {
  isolateComponent,
  labelStyles,
  withModifier,
} from '@zapier/common-utils';

import { skipLinkHref } from '@zapier/universal-nav/dist/constants';

type Props = {
  /**
   * The page content.
   */
  children: ReactNode;
  /**
   * Function to render `UniversalNav`. If it is not provided or if it
   * returns a falsey value, then this component will have no effect on
   * page layout. If a page needs granular control over its layout,
   * return a falsey value for this prop in conjunction with using the
   * `useUniversalNavContext` in the page.
   */
  renderNav?: () => ReactNode;
};

type DerivedState = {
  shouldAccommodateSidebar: boolean;
  sidebarWidth: number;
};

const Styles = labelStyles(__filename, {
  root: (_props: Props, state: DerivedState) => [
    {
      position: 'relative',
      transition: 'padding-left 0.25s ease-in-out',
      willChange: 'padding-left',
    },
    state.shouldAccommodateSidebar &&
      withModifier('accommodateSidebar', {
        paddingLeft: state.sidebarWidth,
      }),
  ],
});

const contentAreaId = skipLinkHref.replace('#', '');

/**
 * Page layout component that handles laying out the overall page
 * structure to accommodate the universal navigation.
 */
export const UniversalNavPageLayout = (props: Props) => {
  const {
    isSidebarControlledByTopbar,
    isSidebarRendered,
    sidebarWidth,
  } = useUniversalNavContext();
  const derivedState = {
    shouldAccommodateSidebar: !isSidebarControlledByTopbar && isSidebarRendered,
    sidebarWidth,
  };
  return (
    <div css={Styles.root(props, derivedState) as Interpolation}>
      <div id={contentAreaId}>{props.children}</div>
    </div>
  );
};
